import type { CurvaDocument } from "@/document/model/document";
import type { DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import { findDocumentStyle } from "@/document/model/documentStyles";
import { oklchToCss, parseColor } from "@/document/model/color";
import { normalizeGradientPaint, normalizeStrokeStack, sampleGradientPaint } from "@/document/model/styles";
import { paintPreviewBackground } from "./paintStyles";

export function documentStyleLabel(style: DocumentStyle): string {
  return style.name;
}

export function documentStylePreviewBackground(document: CurvaDocument, style: DocumentStyle): string {
  switch (style.kind) {
    case "fill": {
      const paint = style.fills.at(-1);
      return paint ? paintPreviewBackground(paint) : "transparent";
    }
    case "stroke": {
      const paint = style.strokes.at(-1)?.paint;
      return paint ? paintPreviewBackground(paint) : "transparent";
    }
    case "text":
      return oklchToCss(parseColor({ l: 0.75, c: 0.02, h: 250, alpha: 1 }));
    case "effects":
      return "linear-gradient(135deg, #64748b 0%, #94a3b8 100%)";
  }
}

export function documentStyleSummary(document: CurvaDocument, styleId: string): string | undefined {
  const style = findDocumentStyle(document, styleId);
  if (!style) {
    return undefined;
  }

  switch (style.kind) {
    case "fill":
      return `${style.fills.length} fill${style.fills.length === 1 ? "" : "s"}`;
    case "stroke":
      return `${style.strokes.length} stroke${style.strokes.length === 1 ? "" : "s"}`;
    case "text":
      return `${style.typography.fontFamily} ${style.typography.fontSize}px`;
    case "effects":
      return `${style.effects.length} effect${style.effects.length === 1 ? "" : "s"}`;
  }
}

export function documentStyleEditorTitle(style: DocumentStyle): string {
  return `Edit ${documentStyleKindLabel(style.kind).toLowerCase()} style`;
}

export function documentStyleKindLabel(kind: DocumentStyleKind): string {
  switch (kind) {
    case "fill":
      return "Fill";
    case "stroke":
      return "Stroke";
    case "text":
      return "Text";
    case "effects":
      return "Effects";
  }
}

export function gradientPreviewCss(style: Extract<DocumentStyle, { kind: "fill" }>): string | undefined {
  const paint = style.fills.find(candidate => candidate.type === "linearGradient" || candidate.type === "radialGradient");
  if (!paint) {
    return undefined;
  }
  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 12)
    .map(stop => `${oklchToCss(stop.color)} ${Math.round(stop.position * 100)}%`)
    .join(", ");
  return normalized.type === "radialGradient"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${normalized.angle + 90}deg, ${stops})`;
}

export function strokeStackWidthSummary(style: Extract<DocumentStyle, { kind: "stroke" }>): string {
  const widths = normalizeStrokeStack({ fills: [], strokes: style.strokes, opacity: 1 }).map(stroke => stroke.width);
  if (widths.length === 0) {
    return "No strokes";
  }
  const unique = [...new Set(widths)];
  return unique.length === 1 ? `${unique[0]}px` : "Mixed widths";
}
